import { FC, ReactNode } from 'react';

type ButtonVariant = 'primary' | 'success' | 'danger' | 'warning' | 'info';

interface ActionButtonProps {
  children: ReactNode;
  onClick: () => void;
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: 'text-blue-700 bg-blue-50 hover:bg-blue-100 border-blue-200 hover:border-blue-300',
  success: 'text-green-700 bg-green-50 hover:bg-green-100 border-green-200 hover:border-green-300',
  danger: 'text-red-700 bg-red-50 hover:bg-red-100 border-red-200 hover:border-red-300',
  warning: 'text-orange-700 bg-orange-50 hover:bg-orange-100 border-orange-200 hover:border-orange-300',
  info: 'text-sky-700 bg-sky-50 hover:bg-sky-100 border-sky-200 hover:border-sky-300',
};

const sizeStyles = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-5 py-2.5 text-base',
};

/**
 * Pure, reusable action button component
 * Feature-agnostic and fully styled with Tailwind
 */
export const ActionButton: FC<ActionButtonProps> = ({
  children,
  onClick,
  variant = 'primary',
  size = 'sm',
  disabled = false,
  className = '',
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`
        inline-flex items-center justify-center gap-2
        font-medium rounded-lg
        border transition-all duration-200
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer active:scale-95'}
        ${className}
      `}
    >
      {children}
    </button>
  );
};
